import React from 'react';
import { formatPercent, orderWeekdayDistribution } from '../summaryFormatters';
import { VIOLATION_VOCAB } from '../summaryVocab';

/**
 * ТЗ v4 §34 (FE-FIX31): распределение событий по дням недели. Доли считает
 * backend, фронт только раскладывает дни в порядке Пн–Вс. День, которого нет
 * в ответе, показывается прочерком, а не нулём.
 *
 * Если в ответе есть previous_*, у каждого дня две наложенные полосы: текущий
 * и прошлый период. Нет прошлого значения у дня — «—», не ноль.
 *
 * BM v2 §2: если есть previous_previous_*, добавляется третья полоса —
 * позапрошлый период. vocab — имена полей счётчика и подписи вкладки.
 */
const SERIES = [
  { id: 'current' },
  { id: 'previous' },
  { id: 'previous_previous' },
];

const seriesKeys = (series, vocab) => {
  if (series.id === 'current') return { count: vocab.countKey, share: 'share_percent' };
  if (series.id === 'previous') return { count: vocab.previousCountKey, share: 'previous_share_percent' };
  return { count: vocab.previousPreviousCountKey, share: 'previous_previous_share_percent' };
};

const shareOf = (item, keys) => Math.max(0, Number(item?.[keys.share]) || 0);

const WeekdayDistributionCard = ({ items, timezoneMode, vocab = VIOLATION_VOCAB }) => {
  const rows = orderWeekdayDistribution(items);
  const hasData = rows.some((row) => row.item);
  const hasSeries = (series) => series.id === 'current'
    || rows.some((row) => row.item && row.item[seriesKeys(series, vocab).count] != null);
  // Третья серия без второй не рисуется: сравнивать не с чем.
  const shown = SERIES.filter((series, index) => (
    hasSeries(series) && (index < 2 || hasSeries(SERIES[1]))
  ));
  const hasPrevious = shown.length > 1;
  // Длина полосы — от самой большой доли среди всех показанных серий, а не
  // от 100%: при равномерном распределении полосы иначе были бы почти равны.
  const maxShare = Math.max(1, ...rows.flatMap(({ item }) => shown.map((series) => (
    item ? shareOf(item, seriesKeys(series, vocab)) : 0
  ))));
  const width = (share) => `${Math.min(100, (share / maxShare) * 100)}%`;

  return (
    <section className="sum-card">
      <h2 className="sum-card-title">Распределение по дням недели</h2>
      {timezoneMode === 'per_camera' && <p className="sum-muted">Локальное время камер</p>}
      {hasData && hasPrevious && (
        <div className="sum-legend">
          <span className="sum-legend-item"><i className="sum-legend-swatch" />Текущий период</span>
          <span className="sum-legend-item"><i className="sum-legend-swatch sum-legend-swatch--prev" />Прошлый период</span>
          {shown.length > 2 && (
            <span className="sum-legend-item"><i className="sum-legend-swatch sum-legend-swatch--prev2" />Позапрошлый период</span>
          )}
        </div>
      )}
      {hasData ? (
        // Горизонтальные полосы: день слева, полоса, справа числа. Полосы
        // периодов наложены: текущая — тонкая и насыщенная спереди, прошлые —
        // толще и бледнее за ней. Длины сравниваются от одной линии отсчёта.
        <ul className="sum-week">
          {rows.map(({ weekday, label, item }) => {
            const values = shown.map((series) => {
              const keys = seriesKeys(series, vocab);
              const known = Boolean(item) && (series.id === 'current' || item[keys.count] != null);
              return { series, keys, known };
            });
            return (
              <li className="sum-week-day" key={weekday}>
                <span className="sum-week-label">{label}</span>
                <span className="sum-week-plot" aria-hidden="true">
                  {[...values].reverse().map(({ series, keys, known }) => (known ? (
                    <span
                      key={series.id}
                      className={`sum-week-bar sum-week-bar--${series.id}`}
                      style={{ width: width(shareOf(item, keys)) }}
                    />
                  ) : null))}
                </span>
                <span className="sum-week-values">
                  {values.map(({ series, keys, known }) => (
                    <span key={series.id} className={`sum-week-value sum-week-value--${series.id}`}>
                      {known ? (
                        <>
                          <b>{item[keys.count] ?? 0}</b>
                          <span className="sum-week-share">{` · ${formatPercent(item[keys.share])}`}</span>
                        </>
                      ) : '—'}
                    </span>
                  ))}
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="sum-muted">{vocab.emptyText}</p>
      )}
    </section>
  );
};

export default WeekdayDistributionCard;
